import { CONTRIBUTOR_KEY } from '../lib/contributor-key';
import { importViewingPlan, initialLocalState, isSeatCandidate, normalizeSeatDecision, normalizeViewingHistoryEntry, normalizeViewingPlan, preserveReviewSeats, validateViewingPlan, type LocalState, type SeatCandidate } from '@anjabom/core';
import { LOCAL_STATE_KEY, type StorageAccess } from './local-state-storage';

export const BEFORE_IMPORT_KEY = 'anjabom.before-import.v1';
export const MAX_BACKUP_BYTES = 5 * 1024 * 1024;
export type LocalBackup = { kind: 'all' | 'plan'; state: LocalState; contributorKey?: string | undefined };
export type ImportMode = 'merge' | 'replace';
const invalid = () => new Error('백업의 구조나 좌석 연결을 확인하지 못했어요. 원본은 바꾸지 않았어요.');

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalid();
  return value as Record<string, unknown>;
}

/** Reject changes a forgiving legacy normalizer would otherwise silently discard. */
function preserved(input: unknown, output: unknown): void {
  if (Array.isArray(input)) {
    if (!Array.isArray(output) || input.length !== output.length) throw invalid();
    if (input.every((item) => typeof item === 'string') && new Set(input).size !== input.length) throw invalid();
    input.forEach((value, index) => { preserved(value, output[index]); });
  } else if (input !== null && typeof input === 'object') {
    const target = object(output);
    for (const [key, value] of Object.entries(input)) {
      if (!Object.hasOwn(target, key)) throw invalid();
      preserved(value, target[key]);
    }
  } else if (input !== output) throw invalid();
}

function unique<T>(rows: T[], key: (value: T) => string): void {
  const ids = rows.map(key);
  if (ids.some((id) => !id) || new Set(ids).size !== ids.length) throw new Error('백업에 중복된 기록 번호가 있어요. 중복을 확인한 파일로 다시 시도해 주세요.');
}

function candidates(value: unknown): SeatCandidate[] {
  if (!Array.isArray(value) || value.length > 4 || value.some((row) => !isSeatCandidate(row))) throw invalid();
  unique(value, (row) => row.candidateId);
  const keys = ['candidateId', 'categoryId', 'venueId', 'venueName', 'spaceId', 'spaceName', 'layoutVersionId', 'instanceId', 'instanceLabel', 'sectionCode', 'rowLabel', 'seatNumber', 'expectedPrice'];
  for (const row of value) {
    if (Object.keys(row).some((key) => !keys.includes(key))) throw invalid();
    for (const key of ['instanceId', 'instanceLabel'] as const) if (row[key] !== undefined && row[key] !== null && typeof row[key] !== 'string') throw invalid();
  }
  return value;
}

export function validateLocalBackupState(value: unknown): LocalState {
  const row = object(value);
  const keys = Object.keys(initialLocalState);
  if (Object.keys(row).some((key) => !keys.includes(key)) || keys.some((key) => key !== 'upcomingPlans' && !Object.hasOwn(row, key))) throw invalid();
  const restoredCandidates = candidates(row.candidates);
  if (!Array.isArray(row.reviews) || !Array.isArray(row.recentVenueIds) || !Array.isArray(row.viewingPlanHistory) || !Array.isArray(row.seatDecisions)) throw invalid();
  unique(row.reviews, (item) => String(object(item).id ?? ''));
  const reviews = row.reviews.map((item) => {
    const review = object(item);
    if (Object.keys(review).some((key) => !['id', 'candidateId', 'candidateSnapshot', 'scores', 'note', 'actualPaid', 'observedAt', 'verification', 'createdAt'].includes(key))) throw invalid();
    if (review.candidateSnapshot !== undefined) {
      const seat = candidates([review.candidateSnapshot])[0];
      if (seat?.candidateId !== review.candidateId) throw invalid();
    }
    for (const key of ['id', 'candidateId', 'note', 'observedAt', 'createdAt']) if (typeof review[key] !== 'string') throw invalid();
    if (review.verification !== 'unverified' || !(review.actualPaid === null || typeof review.actualPaid === 'number' && Number.isFinite(review.actualPaid) && review.actualPaid >= 0)) throw invalid();
    const scores = object(review.scores);
    const dimensions = ['시야', '가림', '소리', '무릎', '의자', '출입'];
    if (Object.keys(scores).length !== dimensions.length || dimensions.some((key) => ![null, 0, 1, 2, 3].includes(scores[key] as number | null))) throw invalid();
    return item as LocalState['reviews'][number];
  });
  const profile = object(row.fitProfile);
  if (Object.keys(profile).length !== 5 || ![null, 'u160', '160-169', '170-179', '180-189', 'o190'].includes(profile.heightBand as string | null) || ![null, true, false].includes(profile.mobilityNeed as boolean | null) || ['knee', 'neckBack', 'soundLight'].some((key) => ![null, 0, 1, 2, 3].includes(profile[key] as number | null))) throw invalid();
  const viewingPlan = normalizeViewingPlan(row.viewingPlan);
  preserved(object(row.viewingPlan), viewingPlan);
  unique(viewingPlan.companionPreferences, (item) => item.id);
  unique(viewingPlan.timelineCheckpoints, (item) => item.kind);
  if (validateViewingPlan(viewingPlan).length) throw invalid();
  const history = row.viewingPlanHistory.map((item) => { const result = normalizeViewingHistoryEntry(item); if (!result) throw invalid(); preserved(item, result); return result; });
  const decisions = row.seatDecisions.map((item) => { candidates(object(item).candidates); const result = normalizeSeatDecision(item); if (!result) throw invalid(); preserved(item, result); return result; });
  unique(history, (item) => item.id);
  unique(decisions, (item) => item.id);
  if (row.recentVenueIds.some((id) => typeof id !== 'string') || new Set(row.recentVenueIds).size !== row.recentVenueIds.length || row.recentVenueIds.length > 5 || history.length > 24 || decisions.length > 24) throw invalid();
  if (row.upcomingPlans !== undefined && (!Array.isArray(row.upcomingPlans) || row.upcomingPlans.length > 12)) throw invalid();
  const upcomingPlans = ((row.upcomingPlans as unknown[] | undefined) ?? []).map((value) => {const plan=normalizeViewingPlan(value);preserved(value,plan);return plan;});
  return preserveReviewSeats({ upcomingPlans, candidates: restoredCandidates, reviews, fitProfile: profile as unknown as LocalState['fitProfile'], viewingPlan, viewingPlanHistory: history, seatDecisions: decisions, recentVenueIds: row.recentVenueIds as string[] });
}

export function exportLocalBackup(state: LocalState): string {
  let contributorKey: string | undefined;
  try { contributorKey = globalThis.localStorage?.getItem(CONTRIBUTOR_KEY) ?? undefined; } catch { throw new Error('기기 열쇠를 읽지 못해 백업을 만들지 않았어요.'); }
  return JSON.stringify({ format: 'anjabom.local-state', version: 2, contributorKey, exportedAt: new Date().toISOString(), photosIncluded: false, state }, null, 2);
}

export function parseLocalBackup(text: string): LocalBackup {
  if (new TextEncoder().encode(text).byteLength > MAX_BACKUP_BYTES) throw new Error('백업은 5MB까지 불러올 수 있어요. 사진이 포함되지 않은 앉아봄 글기록 파일을 골라 주세요.');
  let row: Record<string, unknown>;
  try { row = object(JSON.parse(text)); } catch { throw new Error('백업 파일을 읽지 못했어요. 원본 파일은 그대로 두고 다른 백업을 골라 주세요.'); }
  if ('format' in row || 'version' in row) {
    if (row.format !== 'anjabom.local-state' || ![1, 2].includes(row.version as number)) throw new Error('지원하지 않는 백업 버전이에요. 기록을 바꾸지 않았어요. 최신 앉아봄에서 다시 확인해 주세요.');
    if (Object.keys(row).some((key) => !['format', 'version', 'exportedAt', 'photosIncluded', 'state', 'contributorKey'].includes(key)) || row.photosIncluded !== false) throw invalid();
    if (row.contributorKey !== undefined && (typeof row.contributorKey !== 'string' || !/^[a-f0-9-]{72}$/.test(row.contributorKey))) throw invalid();
    return { kind: 'all', state: validateLocalBackupState(row.state), contributorKey: row.contributorKey as string | undefined };
  }
  if ('schemaVersion' in row) {
    if (![1, 2, 3, 4, 5].includes(row.schemaVersion as number)) throw new Error('지원하지 않는 관람계획 백업 버전이에요. 원본은 바꾸지 않았어요.');
    if (Object.keys(row).some((key) => !['schemaVersion', 'exportedAt', 'photoIncluded', 'plan', 'candidates'].includes(key))) throw invalid();
    const seats = candidates(row.candidates);
    const result = importViewingPlan(text);
    preserved(object(row.plan), result.plan);
    return { kind: 'plan', state: { ...initialLocalState, viewingPlan: result.plan, candidates: seats } };
  }
  return { kind: 'all', state: validateLocalBackupState(row) };
}

function mergeRows<T>(current: T[], incoming: T[], key: (item: T) => string): T[] {
  const ids = new Set(current.map(key));
  return [...current, ...incoming.filter((item) => !ids.has(key(item)))];
}

export function buildBackupRestore(current: LocalState, backup: LocalBackup, mode: ImportMode): LocalState {
  current = preserveReviewSeats(current);
  const incoming = backup.state;
  const result = mode === 'replace'
    ? backup.kind === 'all' ? incoming : { ...current, candidates: incoming.candidates, viewingPlan: incoming.viewingPlan }
    : { ...current,
      candidates: mergeRows(current.candidates, incoming.candidates, (item) => item.candidateId),
      upcomingPlans: mergeRows(current.upcomingPlans, incoming.upcomingPlans, (item)=>item.id??`${item.eventTitle}:${item.startsAt}`),
      reviews: mergeRows(current.reviews, incoming.reviews, (item) => item.id),
      viewingPlanHistory: mergeRows(current.viewingPlanHistory, incoming.viewingPlanHistory, (item) => item.id),
      seatDecisions: mergeRows(current.seatDecisions, incoming.seatDecisions, (item) => item.id),
      recentVenueIds: [...new Set([...current.recentVenueIds, ...incoming.recentVenueIds])].slice(0, 5),
    };
  if (result.upcomingPlans.length > 12 || result.candidates.length > 4 || result.viewingPlanHistory.length > 24 || result.seatDecisions.length > 24) throw new Error('합치면 보관 가능한 후보 4개 또는 기록 24건을 넘어요. 현재 기록을 정리하거나 백업으로 교체를 선택해 주세요. 자동으로 오래된 기록을 지우지 않아요.');
  return validateLocalBackupState(result);
}

export function persistBackupRestore(storage: StorageAccess, state: LocalState, expectedRaw: string | null): { ok: true; raw: string } | { ok: false; message: string } {
  let archived = false;
  try {
    if (storage.getItem(LOCAL_STATE_KEY) !== expectedRaw) return { ok: false, message: '미리보기 뒤 저장된 기록이 바뀌었어요. 원본을 보관하고 백업 파일을 다시 골라 비교해 주세요.' };
    const archive = JSON.stringify({ raw: expectedRaw });
    storage.setItem(BEFORE_IMPORT_KEY, archive);
    if (storage.getItem(BEFORE_IMPORT_KEY) !== archive) return { ok: false, message: '복원 전 원본 보관을 확인하지 못해 적용하지 않았어요.' };
    archived = true;
    const raw = JSON.stringify(state);
    storage.setItem(LOCAL_STATE_KEY, raw);
    if (storage.getItem(LOCAL_STATE_KEY) !== raw) return { ok: false, message: '복원 결과를 다시 읽어 확인하지 못했어요. 복원 전 원본을 보관했으니 내려받고 다시 확인해 주세요.' };
    return { ok: true, raw };
  } catch { return { ok: false, message: archived ? '복원 완료를 확인하지 못했어요. 복원 전 원본은 따로 보관했으니 내려받고 다시 확인해 주세요.' : '복원 전 원본을 보관하지 못해 적용하지 않았어요. 저장 공간을 확인하고 다시 시도해 주세요.' }; }
}

export function readBeforeImport(storage: StorageAccess): string | null {
  try { const row = JSON.parse(storage.getItem(BEFORE_IMPORT_KEY) ?? 'null'); return typeof row?.raw === 'string' ? row.raw : null; } catch { return null; }
}
